import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
import { useUserStore } from "@/entities/user/model";
import { getCurrentUser, logoutUser, loginWithTelegram, } from "@/shared/api/auth";
import { setAuthToken } from "@/shared/api/base";
import { getTelegramWebApp } from "@/shared/lib/telegram";
import { getMiniAppPlatform, waitForMiniAppPlatform, isAnyMiniApp, getAnyInitData, ensureMiniAppBridge, waitForAnyInitData, } from "@/shared/lib/miniApp";
import { clearPendingMusicGeneration } from "@/shared/lib/pendingMusicGeneration";
import { clearSongCreationPersistedState } from "@/shared/lib/songCreationPersistedState";
import { clearChatGenerationPersistedState } from "@/shared/lib/chatGenerationPersistedState";
import { ensureGuestSession, markGuestSessionActive, syncGuestAttributionIfNeeded, clearGuestSessionFlag, } from "@/shared/lib/guestSession";
import { wsManager } from "@/shared/lib/websocket";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
/** Черновик «Создать песню» и ожидания генерации музыки — не переносить между сессиями */
function clearPersistedSessionExtras() {
    clearSongCreationPersistedState();
    clearChatGenerationPersistedState();
    clearPendingMusicGeneration();
}
// login для мини-аппа пишет фронт (на беке нет единого «момента логина»).
// Шлём один раз за загрузку, чтобы StrictMode/повторные initData-авторизации
// не плодили дубли.
let miniAppLoginTracked = false;
function trackMiniAppLoginOnce() {
    if (miniAppLoginTracked) {
        return;
    }
    const platform = getMiniAppPlatform();
    if (!platform) {
        return;
    }
    miniAppLoginTracked = true;
    trackEvent(AnalyticsEvent.LOGIN, {
        method: platform === "telegram" ? "tg_miniapp" : "max_miniapp",
    });
}
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const postEmailAuthActionRef = useRef(null);
    const [telegramAuthFailed, setTelegramAuthFailed] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const schedulePostEmailAuthAction = useCallback((action) => {
        postEmailAuthActionRef.current = action;
    }, []);
    const clearScheduledPostEmailAuthAction = useCallback(() => {
        postEmailAuthActionRef.current = null;
    }, []);
    const flushPostEmailAuthAction = useCallback(() => {
        const fn = postEmailAuthActionRef.current;
        postEmailAuthActionRef.current = null;
        if (!fn) {
            return;
        }
        void (async () => {
            try {
                await wsManager.connect(true);
            }
            catch (e) {
                console.error("WebSocket before post-email-auth action:", e);
            }
            try {
                fn();
            }
            catch (e) {
                console.error("Post-email-auth action error:", e);
            }
        })();
    }, []);
    const { user, isAuthenticated, isLoading, accessToken, setUser, setAuthenticated, setLoading, setTokens, login: loginStore, logout: logoutStore, } = useUserStore();
    const fetchUser = async (initData) => {
        try {
            const userData = await getCurrentUser(initData);
            setUser(userData);
            // Гостевой чекаут: у живого гостя /users/me теперь отдаёт 200 + is_guest.
            // Профиль/балансы гостя сохраняем в стор, но это НЕ авторизация.
            if (userData.is_guest) {
                setAuthenticated(false);
                setIsGuest(true);
                return "guest";
            }
            setAuthenticated(true);
            setIsGuest(false);
            return "authenticated";
        }
        catch (error) {
            // Если ошибка 401 и мы в мини-приложении (TG/MAX), пытаемся
            // переавторизоваться через initData текущей платформы.
            if (isAnyMiniApp() && error?.response?.status === 401) {
                const currentInitData = getAnyInitData();
                if (currentInitData) {
                    try {
                        const authData = await loginWithTelegram(currentInitData);
                        if (authData.user) {
                            loginStore(authData.user, authData.access_token || "", authData.refresh_token || "");
                            if (authData.access_token) {
                                setAuthToken(authData.access_token);
                                setTokens(authData.access_token, authData.refresh_token || null);
                            }
                            setIsGuest(false);
                            return "authenticated";
                        }
                    }
                    catch (reauthError) {
                        console.error("Re-authentication error:", reauthError);
                    }
                }
            }
            setUser(null);
            setAuthenticated(false);
            setTokens(null, null);
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            // Очищаем также данные из Zustand persist storage
            localStorage.removeItem("user-storage");
            // Legacy-fallback: старый бэк отдавал гостю 403 на /users/me. Новый —
            // 200 + is_guest (см. выше). 401/нет токена/сеть — валидной сессии нет.
            const status = error?.response
                ?.status;
            return status === 403 ? "guest" : "none";
        }
    };
    // Функция для извлечения user ID из initData
    const getUserIdFromInitData = (initData) => {
        try {
            const params = new URLSearchParams(initData);
            const userParam = params.get("user");
            if (userParam) {
                const user = JSON.parse(decodeURIComponent(userParam));
                return user.id || null;
            }
        }
        catch (error) {
            console.error("Error parsing initData:", error);
        }
        return null;
    };
    // Функция для проверки соответствия пользователя
    const checkUserMatch = (initData) => {
        const webApp = getTelegramWebApp();
        let currentUserId = null;
        // Сначала пытаемся получить ID из initDataUnsafe (самый надежный способ)
        if (webApp?.initDataUnsafe?.user?.id) {
            currentUserId = webApp.initDataUnsafe.user.id;
        }
        else {
            // Если initDataUnsafe недоступен, извлекаем из initData
            currentUserId = getUserIdFromInitData(initData);
        }
        if (!currentUserId) {
            console.log("Could not extract user ID from initData");
            return false;
        }
        // Если есть сохраненный пользователь, сравниваем telegram_id
        if (user?.telegram_id) {
            const matches = user.telegram_id === currentUserId;
            console.log(`User match check: saved=${user.telegram_id}, current=${currentUserId}, matches=${matches}`);
            return matches;
        }
        // Если нет сохраненного пользователя, считаем что не совпадает (нужна новая авторизация)
        console.log("No saved user, will authorize with new initData");
        return false;
    };
    useEffect(() => {
        const initializeAuth = async () => {
            // Если мы в мини-приложении (Telegram ИЛИ MAX), всегда используем
            // initData для авторизации и не полагаемся на сохранённые токены —
            // они могут быть от другого аккаунта.
            // Мост-скрипты грузятся с `async` (см. index.html), поэтому платформу
            // НЕЛЬЗЯ снимать синхронно — на первом рендере initData может быть ещё не
            // готов и мы бы ушли в гостя («курица-яйцо»). Дожидаемся готовности моста
            // ДО решения «гость vs мини-апп». Для обычного веба вернётся быстро (как
            // только оба моста исполнились с пустым initData).
            const miniAppPlatform = await waitForMiniAppPlatform(3000, 100);
            if (miniAppPlatform) {
                // Подгружаем мост платформы, если хост ещё не инициализировал его.
                await ensureMiniAppBridge();
                // Telegram-специфичный хром (ready/expand/цвет шапки). У MAX-моста
                // этих методов нет — для него блок просто пропускается.
                if (miniAppPlatform === "telegram") {
                    const webApp = getTelegramWebApp();
                    if (webApp) {
                        webApp.ready();
                        try {
                            webApp.expand();
                        }
                        catch {
                            /* expand может быть недоступен в старых клиентах */
                        }
                        /** Единый фон шапки/хрома как в приложении (#101012), без secondary_bg из темы Telegram (часто светлее). */
                        const shellColor = "#101012";
                        const w = webApp;
                        try {
                            w.setBackgroundColor?.(shellColor);
                        }
                        catch {
                            /* ignore */
                        }
                        try {
                            w.setHeaderColor?.(shellColor);
                        }
                        catch {
                            try {
                                w.setHeaderColor?.("bg_color");
                            }
                            catch {
                                /* ignore */
                            }
                        }
                    }
                }
                // Ждём, пока initData текущей платформы станет доступен (с таймаутом)
                const initData = await waitForAnyInitData(3000, 100);
                if (initData) {
                    // Проверяем, соответствует ли текущий initData сохраненному пользователю
                    const userMatches = checkUserMatch(initData);
                    // Если пользователь не совпадает, очищаем старые данные
                    if (!userMatches &&
                        (user || accessToken || localStorage.getItem("accessToken"))) {
                        console.log("User mismatch detected, clearing old data");
                        logoutStore();
                        setAuthToken(null);
                        localStorage.removeItem("accessToken");
                        localStorage.removeItem("refreshToken");
                        // Очищаем также данные из Zustand persist storage
                        localStorage.removeItem("user-storage");
                        clearPersistedSessionExtras();
                    }
                    try {
                        // Авторизуемся через Telegram initData
                        const authData = await loginWithTelegram(initData);
                        // Сохраняем токены (если они есть)
                        if (authData.access_token) {
                            setAuthToken(authData.access_token);
                            setTokens(authData.access_token, authData.refresh_token || null);
                        }
                        // Устанавливаем пользователя
                        if (authData.user) {
                            loginStore(authData.user, authData.access_token || "", authData.refresh_token || "");
                            trackMiniAppLoginOnce();
                        }
                        setTelegramAuthFailed(false);
                    }
                    catch (error) {
                        console.error("Mini app auth error:", error);
                        // Любая ошибка авторизации внутри TG WebApp — фатальна для UX:
                        // у пользователя нет email-флоу, чинить можно только через
                        // бота (нажать /start) или через поддержку. Показываем
                        // заглушку вместо MiniApp с бесполезной модалкой входа.
                        // Сюда попадают: 404 (юзера нет в БД), 401, 5xx, network errors.
                        // Заглушка TG-специфичная («/start в боте»), поэтому для MAX её
                        // не показываем — там пользователь просто увидит WelcomePage.
                        setTelegramAuthFailed(miniAppPlatform === "telegram");
                        // В случае ошибки очищаем данные
                        logoutStore();
                        setAuthToken(null);
                        localStorage.removeItem("accessToken");
                        localStorage.removeItem("refreshToken");
                        // Очищаем также данные из Zustand persist storage
                        localStorage.removeItem("user-storage");
                        clearPersistedSessionExtras();
                    }
                }
                else {
                    console.warn(`Mini app initData is not available after waiting (platform: ${miniAppPlatform}).`, "Telegram WebApp:", !!window.Telegram?.WebApp, "MAX WebApp:", !!window.WebApp);
                    // initData недоступен внутри mini-app ⇢ авторизация невозможна.
                    // Для Telegram показываем заглушку «/start в боте», для MAX —
                    // нет (заглушка TG-специфичная), там будет WelcomePage.
                    setTelegramAuthFailed(miniAppPlatform === "telegram");
                    // Если initData недоступен, очищаем старые данные
                    logoutStore();
                    setAuthToken(null);
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("refreshToken");
                    // Очищаем также данные из Zustand persist storage
                    localStorage.removeItem("user-storage");
                    clearPersistedSessionExtras();
                }
            }
            else {
                // Если не в мини-приложении, используем обычную авторизацию
                // Для веб-версии токены могут быть в куках, поэтому всегда пытаемся получить пользователя
                const token = accessToken || localStorage.getItem("accessToken");
                if (token) {
                    setAuthToken(token);
                }
                // Порядок: сначала проверяем авторизацию пользователя, затем — гостевую
                // сессию. /users/me: 200 — аккаунт, 403 — живая гостевая сессия, иначе нет.
                const status = await fetchUser();
                if (status === "guest") {
                    // Гостевая сессия уже есть — переиспользуем её куки, новую НЕ открываем.
                    // Протухший access-токен внутри окна жизни сессии обновит
                    // 401-интерсептор через /auth/refresh.
                    // Если в URL/storage есть новый click_id — синкаем атрибуцию на /auth/guest.
                    markGuestSessionActive();
                    setIsGuest(true);
                    void syncGuestAttributionIfNeeded();
                }
                else if (status === "none") {
                    // Гостя нет или рефреш протух — открываем новую гостевую сессию, затем
                    // перечитываем /users/me (теперь 200 + is_guest=true), чтобы подтянуть
                    // профиль/балансы гостя для чекаута без регистрации.
                    const created = await ensureGuestSession();
                    if (created) {
                        markGuestSessionActive();
                        const after = await fetchUser();
                        setIsGuest(after !== "authenticated");
                        void syncGuestAttributionIfNeeded();
                    }
                    else {
                        setIsGuest(false);
                    }
                }
            }
            setLoading(false);
        };
        initializeAuth();
        // Обработка возврата в приложение после внешнего перехода (TG и MAX)
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible" && isAnyMiniApp()) {
                // При возврате в приложение проверяем авторизацию
                const initData = getAnyInitData();
                if (initData && isAuthenticated) {
                    // Пытаемся обновить данные пользователя с initData
                    fetchUser(initData).catch((error) => {
                        console.error("Error refreshing user after return:", error);
                    });
                }
            }
        };
        const handleFocus = () => {
            if (isAnyMiniApp()) {
                const initData = getAnyInitData();
                if (initData && isAuthenticated) {
                    fetchUser(initData).catch((error) => {
                        console.error("Error refreshing user on focus:", error);
                    });
                }
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Подключаем WebSocket при входе пользователя и отключаем при выходе
    useEffect(() => {
        if (isAuthenticated && !isLoading) {
            // Подключаемся к WebSocket при входе с флагом поддержания соединения
            console.log("User authenticated, connecting WebSocket...");
            wsManager
                .connect(true) // true = поддерживать соединение всю сессию
                .then(() => {
                console.log("WebSocket connected successfully");
            })
                .catch((error) => {
                console.error("Failed to connect WebSocket:", error);
                // Не блокируем работу приложения, если WebSocket не подключился
            });
        }
        else if (!isAuthenticated && !isLoading) {
            // Отключаемся от WebSocket при выходе
            console.log("User logged out, disconnecting WebSocket...");
            wsManager.disconnect();
        }
    }, [isAuthenticated, isLoading]);
    const login = (userData, accessToken, refreshToken) => {
        setAuthToken(accessToken);
        loginStore(userData, accessToken, refreshToken);
        // Гость залогинился — гостевые куки браузер уже отправил на ручку входа,
        // бэкенд привязал тексты к аккаунту; сбрасываем гостевой признак.
        clearGuestSessionFlag();
        setIsGuest(false);
    };
    const logout = async () => {
        try {
            await logoutUser();
        }
        catch (error) {
            console.error("Logout error:", error);
        }
        finally {
            // Отключаем WebSocket перед выходом
            wsManager.disconnect();
            logoutStore();
            setAuthToken(null);
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            // Очищаем также данные из Zustand persist storage
            localStorage.removeItem("user-storage");
            clearPersistedSessionExtras();
            // Сбрасываем признак гостевой сессии (logoutUser уже погасил куки
            // аккаунта на бэке), затем сразу переключаемся на гостевую сессию.
            clearGuestSessionFlag();
            // После выхода сразу выдаём гостевую сессию (только веб — в мини-аппе
            // гость не используется): бэкенд ставит гостевые куки, и пользователь
            // может продолжать генерировать/покупать без регистрации. Перечитываем
            // /users/me, чтобы подтянуть профиль/балансы нового гостя.
            if (isAnyMiniApp()) {
                setIsGuest(false);
            }
            else {
                const created = await ensureGuestSession();
                if (created) {
                    markGuestSessionActive();
                    const after = await fetchUser();
                    setIsGuest(after !== "authenticated");
                }
                else {
                    setIsGuest(false);
                }
            }
        }
    };
    const refreshUser = async () => {
        await fetchUser();
    };
    return (_jsx(AuthContext.Provider, { value: {
            isAuthenticated,
            isGuest,
            user,
            login,
            logout,
            isLoading,
            setAuthenticated,
            refreshUser,
            schedulePostEmailAuthAction,
            clearScheduledPostEmailAuthAction,
            flushPostEmailAuthAction,
            telegramAuthFailed,
        }, children: children }));
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
